import uniqueId from 'lodash/uniqueId';
import DashboardService from '@/services/DashboardService';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import {
  closeToast,
  editToast,
  showToast,
} from '@/store/slices/components/toast/ToastSlice';

const DOWNLOAD_STATUS_QUEUED = 1;
const DOWNLOAD_STATUS_PROCESSING = 2;
const DOWNLOAD_STATUS_READY = 3;
const DOWNLOAD_STATUS_UNAVAILABLE = 4;
const TRACK_POLL_INTERVAL_MS = 5000;
const POST_DOWNLOAD_DELETE_DELAY_MS = 3000;
const DOWNLOAD_POLL_TIMEOUT_MS = 130000;

const activeDownloadsByTable = {};
const tableDownloadListeners = {};

function normalizeDownloadStatus(status) {
  const parsedStatus = Number(status);
  return Number.isNaN(parsedStatus)
    ? DOWNLOAD_STATUS_UNAVAILABLE
    : parsedStatus;
}

function buildFileDownloadUrl(fileUrl) {
  if (!fileUrl) {
    return null;
  }

  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    return fileUrl;
  }

  const tokkoBrokerHost = localStorage.getItem('tokkoBrokerHost') || '';
  return `${tokkoBrokerHost}${fileUrl}`;
}

function triggerFileDownload(fileUrl) {
  const downloadUrl = buildFileDownloadUrl(fileUrl);
  if (!downloadUrl) {
    return;
  }

  const link = document.createElement('a');
  link.href = downloadUrl;
  link.click();
}

function cancelActiveDownload(tracking) {
  if (!tracking) {
    return Promise.resolve();
  }

  return DashboardService.deleteReportsTableExcelDownload(tracking).catch(
    () => {}
  );
}

function showDownloadError(dispatch, toastId, messageKey = 'DefaultError') {
  dispatch(
    editToast({
      id: toastId,
      text: Trans(translations, messageKey),
      type: 'danger',
      hideCloseIcon: false,
      time: 5000,
    })
  );
}

function notifyTableDownloadListeners(table) {
  const listeners = tableDownloadListeners[table];
  if (!listeners) {
    return;
  }

  const isDownloadInProgress = Boolean(activeDownloadsByTable[table]);
  listeners.forEach((listener) => {
    listener(isDownloadInProgress);
  });
}

function finishTableDownload(table) {
  const activeDownload = activeDownloadsByTable[table];
  if (!activeDownload) {
    return;
  }

  if (activeDownload.pollTimeoutId) {
    clearTimeout(activeDownload.pollTimeoutId);
  }

  delete activeDownloadsByTable[table];
  notifyTableDownloadListeners(table);
}

function pollDownloadStatus(dispatch, table) {
  const activeDownload = activeDownloadsByTable[table];

  if (!activeDownload) {
    return;
  }

  const { toastId, tracking, pollStartedAt } = activeDownload;

  if (Date.now() - pollStartedAt > DOWNLOAD_POLL_TIMEOUT_MS) {
    showDownloadError(dispatch, toastId, 'ExportTimedOut');
    cancelActiveDownload(tracking);
    finishTableDownload(table);
    return;
  }

  DashboardService.trackReportsTableExcelDownload(tracking)
    .then((trackingResponse) => {
      if (!activeDownloadsByTable[table]) {
        return;
      }

      const downloadStatus = normalizeDownloadStatus(trackingResponse.status);

      if (downloadStatus === DOWNLOAD_STATUS_UNAVAILABLE) {
        showDownloadError(dispatch, toastId);
        finishTableDownload(table);
        return;
      }

      if (downloadStatus === DOWNLOAD_STATUS_READY) {
        triggerFileDownload(trackingResponse.file_url);
        dispatch(closeToast(toastId));
        setTimeout(() => {
          cancelActiveDownload(tracking);
        }, POST_DOWNLOAD_DELETE_DELAY_MS);
        finishTableDownload(table);
        return;
      }

      if (
        downloadStatus === DOWNLOAD_STATUS_QUEUED ||
        downloadStatus === DOWNLOAD_STATUS_PROCESSING
      ) {
        activeDownloadsByTable[table].pollTimeoutId = setTimeout(() => {
          pollDownloadStatus(dispatch, table);
        }, TRACK_POLL_INTERVAL_MS);
        return;
      }

      showDownloadError(dispatch, toastId);
      finishTableDownload(table);
    })
    .catch(() => {
      if (!activeDownloadsByTable[table]) {
        return;
      }

      showDownloadError(dispatch, toastId);
      finishTableDownload(table);
    });
}

export function isTableExcelDownloadInProgress(table) {
  return Boolean(activeDownloadsByTable[table]);
}

export function subscribeTableExcelDownloadState(table, listener) {
  if (!tableDownloadListeners[table]) {
    tableDownloadListeners[table] = new Set();
  }

  tableDownloadListeners[table].add(listener);
  listener(Boolean(activeDownloadsByTable[table]));

  return () => {
    tableDownloadListeners[table].delete(listener);
    if (tableDownloadListeners[table].size === 0) {
      delete tableDownloadListeners[table];
    }
  };
}

export function startReportsTableExcelDownload({
  dispatch,
  table,
  downloadPath,
}) {
  if (activeDownloadsByTable[table]) {
    return;
  }

  const toastId = uniqueId('toast_');

  activeDownloadsByTable[table] = {
    toastId,
    tracking: null,
    pollStartedAt: Date.now(),
    pollTimeoutId: null,
  };

  notifyTableDownloadListeners(table);

  dispatch(
    showToast({
      id: toastId,
      text: Trans(translations, 'GeneratingReport'),
      type: 'loading',
      hideCloseIcon: true,
      time: DOWNLOAD_POLL_TIMEOUT_MS + TRACK_POLL_INTERVAL_MS,
    })
  );

  DashboardService.startReportsTableExcelDownload(downloadPath)
    .then((startResponse) => {
      const activeDownload = activeDownloadsByTable[table];

      if (!activeDownload) {
        return;
      }

      const startStatus = normalizeDownloadStatus(startResponse.status);

      if (
        !startResponse.tracking ||
        startStatus === DOWNLOAD_STATUS_UNAVAILABLE
      ) {
        showDownloadError(dispatch, activeDownload.toastId);
        finishTableDownload(table);
        return;
      }

      activeDownloadsByTable[table].tracking = startResponse.tracking;
      pollDownloadStatus(dispatch, table);
    })
    .catch(() => {
      const activeDownload = activeDownloadsByTable[table];

      if (!activeDownload) {
        return;
      }

      showDownloadError(dispatch, activeDownload.toastId);
      finishTableDownload(table);
    });
}
